import React, { useEffect, useRef, useState } from 'react';
import { Spinner, TextInput } from '@producer/ui-kit';
import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons';
import { KeyboardKeys } from '../../assets/enums/keyboard-keys.enum';

import styles from './interactable-text-input.module.scss';
type InteractableTextInputProps = {
  defaultValue?: string;
  label?: string;
  helpText?: string;
  placeholder?: string;
  onSave: (value: string) => Promise<void>;
};

export const InteractableTextInput: React.FC<InteractableTextInputProps> = ({
  defaultValue,
  label,
  helpText,
  placeholder,
  onSave,
}) => {
  const [currentValue, setCurrentValue] = useState(defaultValue);

  const [state, setState] = useState<'loading' | 'success' | 'error' | 'idle'>('idle');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (['idle', 'loading'].includes(state)) {
      return;
    }

    setTimeout(() => {
      setState('idle');
    }, 3100);
  }, [state]);

  const handleBlur = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (e.currentTarget.disabled || !value || value === defaultValue) {
      return;
    }

    setState('loading');

    try {
      await onSave(value);

      setState('success');
    } catch (e) {
      setState('error');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!inputRef?.current) {
      return;
    }

    if (e.key === KeyboardKeys.Escape) {
      setCurrentValue(defaultValue);
      /**
       * Setting the disabled property to true will trigger the onblur event
       * and will not call the handleSubmit function
       * We re-enable it immediately after for further update
       */
      inputRef.current.disabled = true;
      inputRef.current.disabled = false;
    }

    if (e.key === KeyboardKeys.Enter) {
      inputRef.current.blur();
    }
  };

  const renderState = () => {
    switch (state) {
      case 'loading':
        return <Spinner small />;
      case 'success':
        return (
          <span className={styles['state']} data-state="success">
            <CheckIcon />
            Saved
          </span>
        );
      case 'error':
        return (
          <span className={styles['state']} data-state="error">
            <Cross2Icon />
            Could not save
          </span>
        );
      default:
        return;
    }
  };

  return (
    <TextInput
      ref={inputRef}
      label={label}
      hint={helpText}
      placeholder={placeholder || label}
      defaultValue={defaultValue}
      value={currentValue}
      onKeyDown={onKeyDown}
      onBlur={handleBlur}
      onChange={(e) => setCurrentValue(e.target.value)}
      suffix={renderState()}
    />
  );
};
